/**
 * Where a session stands with its reminders (23.09.2026). Pure, so tested.
 * `waiting` = the moment has not come; `due` = it has, and the next run of the
 * job takes it; `missed` = the session started and it never went out.
 */
import { isTeam } from "./userRoles";

export const REMINDER_KINDS = {
  start_48h: { label: "cu 48 de ore înainte", short: "48h" },
  start_1h: { label: "cu o oră înainte", short: "1h" },
};

export const REMINDER_STATES = {
  sent: { label: "trimis", cls: "bg-[#EAF4EE] text-[#2F6B4E]" },
  due: { label: "pleacă la următoarea rulare", cls: "bg-[#FDF3E7] text-[#8a6a1a]" },
  waiting: { label: "așteaptă momentul", cls: "bg-[#EDEEF0] text-[#6B7280]" },
  missed: { label: "nu a plecat", cls: "bg-[#FDECEC] text-[#9b1f2c]" },
};

/** The one line above the table: what needs a person's attention, if anything. */
export function remindersHeadline({ enabled, has_link: hasLink, sessions = [], alert_email: alertEmail }) {
  if (!enabled) return { tone: "off", text: "Reamintirile sunt oprite pentru această grupă." };
  const upcoming = sessions.filter((s) => !s.past);
  if (upcoming.length === 0) return { tone: "off", text: "Nu mai sunt sesiuni viitoare." };
  const withoutLink = upcoming.filter((s) => !s.has_link);
  if (withoutLink.length > 0) {
    return {
      tone: "bad",
      text: `Fără link de acces nu pleacă nicio reamintire (${withoutLink.length} ${
        withoutLink.length === 1 ? "sesiune" : "sesiuni"} viitoare). Completează-l mai sus; `
        + `apoi reamintirile pleacă singure.${alertEmail ? ` Avertismentele merg pe ${alertEmail}.` : ""}`,
    };
  }
  const next = upcoming[0];
  return { tone: "ok", text: `Urmează ${next.label}, către ${next.recipients} ${
    next.recipients === 1 ? "participant" : "participanți"}${hasLink ? "" : " (fără link)"}.` };
}

const DAYS = ["duminică", "luni", "marți", "miercuri", "joi", "vineri", "sâmbătă"];
const MONTHS = ["ianuarie", "februarie", "martie", "aprilie", "mai", "iunie",
  "iulie", "august", "septembrie", "octombrie", "noiembrie", "decembrie"];

/**
 * When a reminder leaves, in words: `sâmbătă, 26 septembrie, ora 09:30`.
 *
 * The server sends Romanian wall-clock time with its offset, so the date and
 * hour in the string are already the ones to show. Read as text rather than
 * converted through the browser's timezone, which would move the hour for
 * anybody working from elsewhere and is exactly the number they need.
 */
export function sendsAt(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(String(iso || ""));
  if (!m) return "";
  const [, y, mo, d, h, min] = m.map(Number);
  const day = DAYS[new Date(Date.UTC(y, mo - 1, d)).getUTCDay()];
  return `${day}, ${d} ${MONTHS[mo - 1]}, ora ${String(h).padStart(2, "0")}:${String(min).padStart(2, "0")}`;
}

/**
 * "Trimite acum" on one reminder: a future session with its link, someone
 * still waiting for it — and the team at the keyboard (19.10.2026), since the
 * server sends it only for owner and Admin FPED.
 */
export function canSendNow(session, kind, { canMutate, role } = {}) {
  return Boolean(canMutate && isTeam(role) && session && !session.past && session.has_link
    && kind && kind.sent < session.recipients);
}

/** The line under a reminder's name: when it goes out, and why not earlier. */
export function reminderWhen(k) {
  const at = sendsAt(k?.send_at || k?.due_at);
  if (!at) return "";
  return k?.held_by_window ? `${at} (amânată până la ora 08:00)` : at;
}
